import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Locale, Role, UserSummary } from '@displaycms/contracts';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { useAuth } from '@/auth/auth-context';
import {
  createUser,
  deleteUser,
  importUsersCsv,
  listUsers,
  updateUser,
  type ImportCsvResult,
  type UserListResponse,
} from '@/api/users';

/**
 * `/users` — table of users with create / edit / delete.
 *
 * Visibility is gated at the route level via `RequireAuth` + a permission
 * check on `users.read` (RBAC permission defined in
 * `apps/server/src/auth/roles.guard.ts`). Other actions are gated per-row.
 *
 * Server endpoints (POST/PATCH/DELETE /v1/admin/users) DO NOT exist yet
 * at Task 29; the API wrappers will 404 at runtime. The page surfaces
 * load-errors gracefully.
 */

const ALL_ROLES: ReadonlyArray<Role> = ['admin', 'operator', 'auditor'];
const ALL_LOCALES: ReadonlyArray<Locale> = ['fr', 'en'];

type DialogMode = { kind: 'create' } | { kind: 'edit'; user: UserSummary } | null;

export function UsersPage(): JSX.Element {
  const { t } = useTranslation(['users', 'common']);
  const { hasPermission } = useAuth();
  const canRead = hasPermission('users.read');
  const canCreate = hasPermission('users.create');
  const canUpdate = hasPermission('users.update');
  const canDelete = hasPermission('users.delete');

  if (!canRead) {
    return (
      <div className="flex flex-col gap-4" data-testid="users-page">
        <Alert variant="danger" data-testid="users-forbidden">
          {t('users:forbidden')}
        </Alert>
      </div>
    );
  }

  return (
    <UsersPageContent
      canCreate={canCreate}
      canUpdate={canUpdate}
      canDelete={canDelete}
    />
  );
}

interface UsersPageContentProps {
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

function UsersPageContent({
  canCreate,
  canUpdate,
  canDelete,
}: UsersPageContentProps): JSX.Element {
  const { t } = useTranslation(['users', 'common']);
  const queryClient = useQueryClient();

  const usersQuery = useQuery<UserListResponse>({
    queryKey: ['users', 'list'],
    queryFn: () => listUsers(),
    staleTime: 30_000,
  });

  const users = usersQuery.data ?? [];

  const [dialog, setDialog] = useState<DialogMode>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserSummary | null>(null);
  const [showImport, setShowImport] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const invalidate = useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: ['users', 'list'] });
  }, [queryClient]);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => deleteUser(id),
    onSuccess: () => {
      setError(null);
      setDeleteTarget(null);
      invalidate();
    },
    onError: () => {
      setError(t('users:delete_dialog.errors.delete_failed'));
    },
  });

  return (
    <div className="flex flex-col gap-4" data-testid="users-page">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('users:page_title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('users:page_subtitle')}</p>
        </div>
        <div className="flex gap-2">
          {canCreate && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowImport(true)}
              data-testid="users-import-btn"
            >
              {t('users:import_csv')}
            </Button>
          )}
          {canCreate && (
            <Button
              type="button"
              onClick={() => setDialog({ kind: 'create' })}
              data-testid="new-user"
            >
              {t('users:new_user')}
            </Button>
          )}
        </div>
      </header>

      {error && (
        <Alert variant="danger" data-testid="users-error">
          {error}
        </Alert>
      )}

      {usersQuery.isLoading && (
        <p
          role="status"
          data-testid="users-loading"
          className="text-sm text-muted-foreground"
        >
          {t('users:list_loading')}
        </p>
      )}
      {usersQuery.isError && (
        <p
          role="alert"
          data-testid="users-load-error"
          className="text-sm text-destructive"
        >
          {t('users:list_error')}
        </p>
      )}

      {!usersQuery.isLoading && !usersQuery.isError && users.length === 0 && (
        <p
          data-testid="users-empty"
          className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground"
        >
          {t('users:empty_state')}
        </p>
      )}

      {users.length > 0 && (
        <table
          data-testid="users-table"
          className="w-full overflow-hidden rounded-md border border-border bg-card text-sm"
        >
          <thead className="bg-muted text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2">{t('users:column.username')}</th>
              <th className="px-3 py-2">{t('users:column.display_name')}</th>
              <th className="px-3 py-2">{t('users:column.roles')}</th>
              <th className="px-3 py-2">{t('users:column.locale')}</th>
              <th className="px-3 py-2 text-right">{t('users:column.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.id}
                data-testid={`user-row-${u.id}`}
                className="border-t border-border"
              >
                <td className="px-3 py-2 font-mono text-xs">{u.username}</td>
                <td className="px-3 py-2">{u.display_name}</td>
                <td className="px-3 py-2">
                  <ul className="flex flex-wrap gap-1">
                    {u.roles.map((r) => (
                      <li
                        key={r}
                        className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground"
                        data-testid={`user-role-${u.id}-${r}`}
                      >
                        {t(`users:roles.${r}`)}
                      </li>
                    ))}
                  </ul>
                </td>
                <td className="px-3 py-2 text-xs uppercase">
                  {t(`users:locale.${u.locale}`)}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    {canUpdate && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setDialog({ kind: 'edit', user: u })}
                        data-testid={`edit-user-${u.id}`}
                      >
                        {t('users:row.edit')}
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setDeleteTarget(u)}
                        data-testid={`delete-user-${u.id}`}
                      >
                        {t('users:row.delete')}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {dialog?.kind === 'create' && (
        <CreateUserDialog
          onClose={() => setDialog(null)}
          onCreated={() => {
            setDialog(null);
            invalidate();
          }}
        />
      )}

      {dialog?.kind === 'edit' && (
        <EditUserDialog
          user={dialog.user}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            invalidate();
          }}
        />
      )}

      {deleteTarget && (
        <DeleteUserDialog
          user={deleteTarget}
          isDeleting={deleteMutation.isPending}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        />
      )}

      {showImport && (
        <ImportCsvDialog
          onClose={() => setShowImport(false)}
          onImported={() => {
            // Don't close the dialog here — keep it open so the user can read
            // the import summary. Only invalidate the user list in the background.
            invalidate();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CreateUserDialog
// ---------------------------------------------------------------------------

interface CreateUserDialogProps {
  onClose: () => void;
  onCreated: (created: UserSummary) => void;
}

function CreateUserDialog({ onClose, onCreated }: CreateUserDialogProps): JSX.Element {
  const { t } = useTranslation(['users']);
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>('');
  const [locale, setLocale] = useState<Locale>('fr');
  const [roles, setRoles] = useState<Set<Role>>(new Set());
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = useCallback(async (): Promise<void> => {
    if (username.trim().length === 0) {
      setErr(t('users:create_dialog.errors.missing_username'));
      return;
    }
    if (password.length < 8) {
      setErr(t('users:create_dialog.errors.missing_password'));
      return;
    }
    if (displayName.trim().length === 0) {
      setErr(t('users:create_dialog.errors.missing_display_name'));
      return;
    }
    if (roles.size === 0) {
      setErr(t('users:create_dialog.errors.missing_role'));
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const created = await createUser({
        username: username.trim(),
        password,
        display_name: displayName.trim(),
        locale,
        roles: Array.from(roles),
      });
      onCreated(created);
    } catch {
      setErr(t('users:create_dialog.errors.create_failed'));
    } finally {
      setSubmitting(false);
    }
  }, [username, password, displayName, locale, roles, onCreated, t]);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="create-user-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('users:create_dialog.title')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Field label={t('users:create_dialog.username_label')}>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('users:create_dialog.username_placeholder')}
              data-testid="create-user-username"
              autoFocus
            />
          </Field>
          <Field
            label={t('users:create_dialog.password_label')}
            help={t('users:create_dialog.password_help')}
          >
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="create-user-password"
            />
          </Field>
          <Field label={t('users:create_dialog.display_name_label')}>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t('users:create_dialog.display_name_placeholder')}
              data-testid="create-user-display-name"
            />
          </Field>
          <Field label={t('users:create_dialog.locale_label')}>
            <Select value={locale} onValueChange={(v) => setLocale(v as Locale)}>
              <SelectTrigger className="h-9 w-full" data-testid="create-user-locale">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ALL_LOCALES.map((l) => (
                  <SelectItem key={l} value={l}>
                    {t(`users:locale.${l}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <RoleChecklist
            value={roles}
            onChange={setRoles}
            testIdPrefix="create-user-role"
          />
        </div>
        {err && (
          <Alert variant="danger" className="mt-2" data-testid="create-user-error">
            {err}
          </Alert>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="create-user-cancel"
          >
            {t('users:create_dialog.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void submit();
            }}
            disabled={submitting}
            data-testid="create-user-submit"
          >
            {submitting
              ? t('users:create_dialog.creating')
              : t('users:create_dialog.create')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// EditUserDialog
// ---------------------------------------------------------------------------

interface EditUserDialogProps {
  user: UserSummary;
  onClose: () => void;
  onSaved: (saved: UserSummary) => void;
}

function EditUserDialog({ user, onClose, onSaved }: EditUserDialogProps): JSX.Element {
  const { t } = useTranslation(['users']);
  const [displayName, setDisplayName] = useState<string>(user.display_name);
  const [locale, setLocale] = useState<Locale>(user.locale);
  const [roles, setRoles] = useState<Set<Role>>(new Set(user.roles));
  const [saving, setSaving] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);

  const save = useCallback(async (): Promise<void> => {
    if (roles.size === 0) {
      setErr(t('users:edit_dialog.errors.missing_role'));
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const saved = await updateUser(user.id, {
        display_name: displayName.trim(),
        locale,
        roles: Array.from(roles),
      });
      onSaved(saved);
    } catch {
      setErr(t('users:edit_dialog.errors.save_failed'));
    } finally {
      setSaving(false);
    }
  }, [displayName, locale, roles, user.id, onSaved, t]);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="edit-user-dialog"
        className="max-w-md"
        aria-label={t('users:edit_dialog.title', { name: user.display_name })}
      >
        <DialogHeader>
          <DialogTitle>
            {t('users:edit_dialog.title', { name: user.display_name })}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Field label={t('users:edit_dialog.display_name_label')}>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              data-testid="edit-user-display-name"
            />
          </Field>
          <Field label={t('users:edit_dialog.locale_label')}>
            <Select value={locale} onValueChange={(v) => setLocale(v as Locale)}>
              <SelectTrigger className="h-9 w-full" data-testid="edit-user-locale">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ALL_LOCALES.map((l) => (
                  <SelectItem key={l} value={l}>
                    {t(`users:locale.${l}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <RoleChecklist
            value={roles}
            onChange={setRoles}
            testIdPrefix="edit-user-role"
          />
        </div>
        {err && (
          <Alert variant="danger" className="mt-2" data-testid="edit-user-error">
            {err}
          </Alert>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="edit-user-cancel"
          >
            {t('users:edit_dialog.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void save();
            }}
            disabled={saving}
            data-testid="edit-user-submit"
          >
            {saving ? t('users:edit_dialog.saving') : t('users:edit_dialog.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// DeleteUserDialog
// ---------------------------------------------------------------------------

interface DeleteUserDialogProps {
  user: UserSummary;
  isDeleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function DeleteUserDialog({
  user,
  isDeleting,
  onClose,
  onConfirm,
}: DeleteUserDialogProps): JSX.Element {
  const { t } = useTranslation(['users']);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="delete-user-dialog"
        className="max-w-md"
        aria-label={t('users:delete_dialog.title')}
      >
        <DialogHeader>
          <DialogTitle>{t('users:delete_dialog.title')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm">
          {t('users:delete_dialog.body', { name: user.display_name })}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="delete-user-cancel"
          >
            {t('users:delete_dialog.cancel')}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting}
            data-testid="delete-user-confirm"
          >
            {isDeleting
              ? t('users:delete_dialog.deleting')
              : t('users:delete_dialog.confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// ImportCsvDialog
// ---------------------------------------------------------------------------

interface ImportCsvDialogProps {
  onClose: () => void;
  onImported: () => void;
}

function ImportCsvDialog({ onClose, onImported }: ImportCsvDialogProps): JSX.Element {
  const { t } = useTranslation(['users']);
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);
  const [summary, setSummary] = useState<ImportCsvResult | null>(null);

  const submit = useCallback(async (): Promise<void> => {
    if (!file) {
      setErr(t('users:import_dialog.errors.no_file'));
      return;
    }
    setImporting(true);
    setErr(null);
    setSummary(null);
    try {
      const result = await importUsersCsv(file);
      setSummary(result);
      // Invalidate the user list so it refreshes when the dialog is closed,
      // but keep the dialog open so the user can read the summary.
      onImported();
    } catch {
      setErr(t('users:import_dialog.errors.import_failed'));
    } finally {
      setImporting(false);
    }
  }, [file, onImported, t]);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="import-csv-dialog"
        className="max-w-md"
        aria-label={t('users:import_dialog.title')}
      >
        <DialogHeader>
          <DialogTitle>{t('users:import_dialog.title')}</DialogTitle>
        </DialogHeader>
        <p className="mb-3 text-xs text-muted-foreground">{t('users:import_dialog.description')}</p>

        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('users:import_dialog.select_file')}</span>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => {
                const picked = e.target.files?.[0] ?? null;
                setFile(picked);
                setSummary(null);
                setErr(null);
              }}
              data-testid="users-import-file"
              className="text-sm"
            />
          </label>
        </div>

        {err && (
          <Alert variant="danger" className="mt-2" data-testid="import-csv-error">
            {err}
          </Alert>
        )}

        {summary !== null && (
          <div
            className="mt-3 rounded-md border border-border bg-muted p-3 text-sm"
            data-testid="users-import-summary"
          >
            <p>
              {t('users:import_summary', {
                created: summary.created,
                skipped: summary.skipped,
                errors: summary.errors.length,
              })}
            </p>
            {summary.errors.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs text-destructive">
                {summary.errors.map((e) => (
                  <li key={e.row}>
                    Row {e.row}: {e.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="import-csv-cancel"
          >
            {t('users:import_dialog.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void submit();
            }}
            disabled={importing || !file}
            data-testid="import-csv-submit"
          >
            {importing
              ? t('users:import_dialog.importing')
              : t('users:import_dialog.import')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Field + RoleChecklist helpers
// ---------------------------------------------------------------------------

interface FieldProps {
  label: string;
  help?: string;
  children: React.ReactNode;
}

function Field({ label, help, children }: FieldProps): JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium">{label}</span>
      {children}
      {help && <span className="text-xs text-muted-foreground">{help}</span>}
    </label>
  );
}

interface RoleChecklistProps {
  value: Set<Role>;
  onChange: (next: Set<Role>) => void;
  testIdPrefix: string;
}

function RoleChecklist({ value, onChange, testIdPrefix }: RoleChecklistProps): JSX.Element {
  const { t } = useTranslation(['users']);
  const items = useMemo(
    () =>
      ALL_ROLES.map((r) => ({
        role: r,
        label: t(`users:roles.${r}`),
      })),
    [t],
  );
  return (
    <fieldset className="flex flex-col gap-1 text-sm">
      <legend className="mb-1 font-medium">{t('users:create_dialog.roles_label')}</legend>
      {items.map(({ role, label }) => (
        <label key={role} className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={value.has(role)}
            onChange={(e) => {
              const next = new Set(value);
              if (e.target.checked) next.add(role);
              else next.delete(role);
              onChange(next);
            }}
            data-testid={`${testIdPrefix}-${role}`}
          />
          <span>{label}</span>
        </label>
      ))}
    </fieldset>
  );
}

export default UsersPage;
